import {
  Design,
  Areas,
  Flow,
  FlowStop,
  Group,
  InterAreaConnection,
  JustInTimeRoutingConfig,
  LoadType,
  LogicalSpace,
  Obstacle,
  VehicleType,
  Floorplan,
  Track,
  TrafficRulesCatalog,
  MetaData,
  FlowLayer,
  Templates,
  FloorplanRevision,
  ReferenceImage,
  UxOnly,
  FileReference,
} from '@thive/fddm-types';

import { logger } from '@/modules/common/logging/logger';
import { Area, Template } from '@/modules/floorplanDesign/types';
import { FddmPreferenceValues } from '@/modules/floorplanDesign/preferences';

import { FddmItemUpdates, IFddmService, TemplateRegeneration } from './IFddmService';

export class FddmServiceLogDecorator implements IFddmService {
  private decoree: IFddmService;

  constructor(decoree: IFddmService) {
    this.decoree = decoree;
  }

  initialize(revision: FloorplanRevision): void {
    const start = this.logStart('initialize');
    this.decoree.initialize(revision);
    this.logEnd('initialize', start);
  }

  async regenerateAll(): Promise<void> {
    const start = this.logStart('regenerateAll');
    await this.decoree.regenerateAll();
    this.logEnd('regenerateAll', start);
  }

  getModel(): FloorplanRevision {
    const start = this.logStart('getModel');
    const model = this.decoree.getModel();
    this.logEnd('getModel', start);
    return model;
  }

  // MetaData

  getMetaData(): MetaData {
    const start = this.logStart('getMetaData');
    const metaData = this.decoree.getMetaData();
    this.logEnd('getMetaData', start);
    return metaData;
  }

  setMetaData(metaData: MetaData): void {
    const start = this.logStart('setMetaData');
    this.decoree.setMetaData(metaData);
    this.logEnd('setMetaData', start);
  }

  //
  // Reference image
  //

  getReferenceImage(): UxOnly['referenceImage'] {
    const start = this.logStart('getReferenceImage');
    const value = this.decoree.getReferenceImage();
    this.logEnd('getReferenceImage', start);
    return value;
  }

  addReferenceImage(referenceImage: ReferenceImage): void {
    const start = this.logStart('addReferenceImage');
    this.decoree.addReferenceImage(referenceImage);
    this.logEnd('addReferenceImage', start);
  }

  updateReferenceImage(referenceImage: ReferenceImage): void {
    const start = this.logStart('updateReferenceImage');
    this.decoree.updateReferenceImage(referenceImage);
    this.logEnd('updateReferenceImage', start);
  }

  deleteReferenceImage(): void {
    const start = this.logStart('deleteReferenceImage');
    this.decoree.deleteReferenceImage();
    this.logEnd('deleteReferenceImage', start);
  }

  //
  // Point cloud
  //

  addPointCloudInputFileRef(fileRef: string): void {
    const start = this.logStart('addPointCloudInputFileRef');
    this.decoree.addPointCloudInputFileRef(fileRef);
    this.logEnd('addPointCloudInputFileRef', start);
  }

  addPointCloudOutputFileRef(fileRef: string): void {
    const start = this.logStart('addPointCloudOutputFileRef');
    this.decoree.addPointCloudOutputFileRef(fileRef);
    this.logEnd('addPointCloudOutputFileRef', start);
  }

  deletePointCloudInputFileRef(): void {
    const start = this.logStart('deletePointCloudInputFileRef');
    this.decoree.deletePointCloudInputFileRef();
    this.logEnd('deletePointCloudInputFileRef', start);
  }

  deletePointCloudOutputFileRef(): void {
    const start = this.logStart('deletePointCloudOutputFileRef');
    this.decoree.deletePointCloudOutputFileRef();
    this.logEnd('deletePointCloudOutputFileRef', start);
  }

  //
  // Design
  //

  getDesign(): Design {
    const start = this.logStart('getDesign');
    const design = this.decoree.getDesign();
    this.logEnd('getDesign', start);
    return design;
  }

  setDesign(design: Design): void {
    const start = this.logStart('setDesign');
    this.decoree.setDesign(design);
    this.logEnd('setDesign', start);
  }

  //
  // Areas
  //

  getArea(id: string): Area | undefined {
    const start = this.logStart('getArea');
    const area: Area | undefined = this.decoree.getArea(id);
    this.logEnd('getArea', start);
    return area;
  }

  async addAreas(areas: Areas): Promise<void> {
    const start = this.logStart('addAreas');
    await this.decoree.addAreas(areas);
    this.logEnd('addAreas', start);
  }

  updateArea(area: Area): void {
    const start = this.logStart('updateArea');
    this.decoree.updateArea(area);
    this.logEnd('updateArea', start);
  }

  updateAreas(areas: Areas): void {
    const start = this.logStart('updateAreas');
    this.decoree.updateAreas(areas);
    this.logEnd('updateAreas', start);
  }

  deleteAreas(areas: Areas): void {
    const start = this.logStart('deleteAreas');
    this.decoree.deleteAreas(areas);
    this.logEnd('deleteAreas', start);
  }

  //
  // Obstacles
  //

  getObstacle(id: string): Obstacle | undefined {
    const start = this.logStart('getObstacle');
    const obstacle: Obstacle | undefined = this.decoree.getObstacle(id);
    this.logEnd('getObstacle', start);
    return obstacle;
  }

  async addObstacles(obstacles: Obstacle[]): Promise<void> {
    const start = this.logStart('addObstacles');
    await this.decoree.addObstacles(obstacles);
    this.logEnd('addObstacles', start);
  }

  updateObstacle(obstacle: Obstacle): void {
    const start = this.logStart('updateObstacle');
    this.decoree.updateObstacle(obstacle);
    this.logEnd('updateObstacle', start);
  }

  updateObstacles(obstacles: Obstacle[]): void {
    const start = this.logStart('updateObstacles');
    this.decoree.updateObstacles(obstacles);
    this.logEnd('updateObstacles', start);
  }

  updateItems(items: FddmItemUpdates): void {
    const start = this.logStart('updateItems');
    this.decoree.updateItems(items);
    this.logEnd('updateItems', start);
  }

  deleteObstacles(obstacles: Obstacle[]): void {
    const start = this.logStart('deleteObstacles');
    this.decoree.deleteObstacles(obstacles);
    this.logEnd('deleteObstacles', start);
  }

  //
  // Inter Area Connections
  //

  getInterAreaConnection(id: string): InterAreaConnection | undefined {
    const start = this.logStart('getInterAreaConnection');
    const connection: InterAreaConnection | undefined = this.decoree.getInterAreaConnection(id);
    this.logEnd('getInterAreaConnection', start);
    return connection;
  }

  addInterAreaConnections(connections: InterAreaConnection[]): boolean {
    const start = this.logStart('addInterAreaConnections');
    const result = this.decoree.addInterAreaConnections(connections);
    this.logEnd('addInterAreaConnections', start);
    return result;
  }

  updateInterAreaConnections(connections: InterAreaConnection[]): void {
    const start = this.logStart('updateInterAreaConnections');
    this.decoree.updateInterAreaConnections(connections);
    this.logEnd('updateInterAreaConnections', start);
  }

  deleteInterAreaConnections(connections: InterAreaConnection[]): void {
    const start = this.logStart('deleteInterAreaConnections');
    this.decoree.deleteInterAreaConnections(connections);
    this.logEnd('deleteInterAreaConnections', start);
  }

  //
  // Logical Spaces
  //

  getLogicalSpace(id: string): LogicalSpace | undefined {
    const start = this.logStart('getLogicalSpace');
    const ls: LogicalSpace | undefined = this.decoree.getLogicalSpace(id);
    this.logEnd('getLogicalSpace', start);
    return ls;
  }

  async addLogicalSpaces(logicalSpaces: LogicalSpace[]): Promise<void> {
    const start = this.logStart('addLogicalSpaces');
    await this.decoree.addLogicalSpaces(logicalSpaces);
    this.logEnd('addLogicalSpaces', start);
  }

  updateLogicalSpace(logicalSpace: LogicalSpace): void {
    const start = this.logStart(`updateLogicalSpace ${logicalSpace.id}`);
    this.decoree.updateLogicalSpace(logicalSpace);
    this.logEnd(`updateLogicalSpace ${logicalSpace.id}`, start);
  }

  deleteLogicalSpaces(logicalSpaces: LogicalSpace[]): void {
    const start = this.logStart('deleteLogicalSpaces');
    this.decoree.deleteLogicalSpaces(logicalSpaces);
    this.logEnd('deleteLogicalSpaces', start);
  }

  //
  //  Templates
  //

  getTemplate(id: string): Template | undefined {
    const start = this.logStart('getTemplate');
    const template: Template | undefined = this.decoree.getTemplate(id);
    this.logEnd('getTemplate', start);
    return template;
  }

  addTemplates(templates: Templates): void {
    const start = this.logStart('addTemplates');
    this.decoree.addTemplates(templates);
    this.logEnd('addTemplates', start);
  }

  deleteTemplates(templates: Templates): void {
    const start = this.logStart('deleteTemplates');
    this.decoree.deleteTemplates(templates);
    this.logEnd('deleteTemplates', start);
  }

  async addTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
  ): Promise<void> {
    const start = this.logStart('addTemplate');
    await this.decoree.addTemplate(template, areas, interAreaConnections, flowLayer);
    this.logEnd('addTemplate', start);
  }

  updateTemplate(template: Template): void {
    const start = this.logStart('updateTemplate');
    this.decoree.updateTemplate(template);
    this.logEnd('updateTemplate', start);
  }

  updateTemplates(templates: Templates): void {
    const start = this.logStart('updateTemplates');
    this.decoree.updateTemplates(templates);
    this.logEnd('updateTemplates', start);
  }

  async regenerateTemplates(regenerations: TemplateRegeneration[]): Promise<void> {
    const start = this.logStart('regenerateTemplates');
    await this.decoree.regenerateTemplates(regenerations);
    this.logEnd('regenerateTemplates', start);
  }

  //
  // Groups
  //

  getGroup(id: string): Group | undefined {
    const start = this.logStart('getGroup');
    const group = this.decoree.getGroup(id);
    this.logEnd('getGroup', start);
    return group;
  }

  addGroups(groups: Group[]): void {
    const start = this.logStart('addGroups');
    this.decoree.addGroups(groups);
    this.logEnd('addGroups', start);
  }

  updateGroup(group: Group): void {
    const start = this.logStart('updateGroup');
    this.decoree.updateGroup(group);
    this.logEnd('updateGroup', start);
  }

  deleteGroups(groups: Group[]): void {
    const start = this.logStart('deleteGroups');
    this.decoree.deleteGroups(groups);
    this.logEnd('deleteGroups', start);
  }

  ungroupGroups(groups: Group[]): void {
    const start = this.logStart('ungroupGroups');
    this.decoree.ungroupGroups(groups);
    this.logEnd('ungroupGroups', start);
  }

  //
  // Jit Routing
  //

  getJitRoutingConfig(id: string): JustInTimeRoutingConfig | undefined {
    const start = this.logStart('getJitRoutingConfig');
    const jitRoutingConfig: JustInTimeRoutingConfig | undefined =
      this.decoree.getJitRoutingConfig(id);
    this.logEnd('getJitRoutingConfig', start);
    return jitRoutingConfig;
  }

  addJitRoutingConfigs(jitRoutingPoints: JustInTimeRoutingConfig[]): void {
    const start = this.logStart('addJitRoutingConfigs');
    this.decoree.addJitRoutingConfigs(jitRoutingPoints);
    this.logEnd('addJitRoutingConfigs', start);
  }

  updateJitRoutingConfig(jitRoutingPoint: JustInTimeRoutingConfig): void {
    const start = this.logStart('updateJitRoutingConfig');
    this.decoree.updateJitRoutingConfig(jitRoutingPoint);
    this.logEnd('updateJitRoutingConfig', start);
  }

  deleteJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    const start = this.logStart('deleteJitRoutingConfigs');
    this.decoree.deleteJitRoutingConfigs(jitRoutingConfigs);
    this.logEnd('deleteJitRoutingConfigs', start);
  }

  //
  // Flows
  //

  getFlow(id: string): Flow | undefined {
    const start = this.logStart('getFlow');
    const flow: Flow | undefined = this.decoree.getFlow(id);
    this.logEnd('getFlow', start);
    return flow;
  }

  getFlowsByAreaId(areaId: string): Flow[] | undefined {
    const start = this.logStart('getFlowsByAreaId');
    const flows: Flow[] | undefined = this.decoree.getFlowsByAreaId(areaId);
    this.logEnd('getFlowsByAreaId', start);
    return flows;
  }

  addFlows(flows: Flow[]): void {
    const start = this.logStart('addFlows');
    this.decoree.addFlows(flows);
    this.logEnd('addFlows', start);
  }

  updateFlow(flow: Flow): void {
    const start = this.logStart('updateFlow');
    this.decoree.updateFlow(flow);
    this.logEnd('updateFlow', start);
  }

  updateFlows(flows: Flow[]): void {
    const start = this.logStart('updateFlows');
    this.decoree.updateFlows(flows);
    this.logEnd('updateFlows', start);
  }

  deleteFlows(flows: Flow[]): void {
    const start = this.logStart('deleteFlows');
    this.decoree.deleteFlows(flows);
    this.logEnd('deleteFlows', start);
  }

  //
  // FlowStops
  //

  getFlowStop(id: string): FlowStop | undefined {
    const start = this.logStart('getFlowStop');
    const flowStop = this.decoree.getFlowStop(id);
    this.logEnd('getFlowStop', start);
    return flowStop;
  }

  getFlowStopByAreaId(areaId: string): FlowStop | undefined {
    const start = this.logStart('getFlowStopByAreaId');
    const flowStop = this.decoree.getFlowStopByAreaId(areaId);
    this.logEnd('getFlowStopByAreaId', start);
    return flowStop;
  }

  addFlowStops(flowStops: FlowStop[]): void {
    const start = this.logStart('addFlowStops');
    this.decoree.addFlowStops(flowStops);
    this.logEnd('addFlowStops', start);
  }

  updateFlowStop(flowStop: FlowStop): void {
    const start = this.logStart('updateFlowStop');
    this.decoree.updateFlowStop(flowStop);
    this.logEnd('updateFlowStop', start);
  }

  deleteFlowStops(flowStops: FlowStop[]): void {
    const start = this.logStart('deleteFlowStops');
    this.decoree.deleteFlowStops(flowStops);
    this.logEnd('deleteFlowStops', start);
  }

  //
  // Vehicles
  //

  getVehicleType(id: string): VehicleType | undefined {
    const start = this.logStart('getVehicleType');
    const vehicleType: VehicleType | undefined = this.decoree.getVehicleType(id);
    this.logEnd('getVehicleType', start);
    return vehicleType;
  }

  addVehicleTypes(vehicleTypes: VehicleType[]): void {
    const start = this.logStart('addVehicleTypes');
    this.decoree.addVehicleTypes(vehicleTypes);
    this.logEnd('addVehicleTypes', start);
  }

  addVehiclesToHighways(vehicleTypes: FloorplanRevision['setup']['vehicleTypes']): void {
    const start = this.logStart('addVehiclesToHighways');
    this.decoree.addVehiclesToHighways(vehicleTypes);
    this.logEnd('addVehiclesToHighways', start);
  }

  updateVehicleType(vehicleType: VehicleType): void {
    const start = this.logStart('updateVehicleType');
    this.decoree.updateVehicleType(vehicleType);
    this.logEnd('updateVehicleType', start);
  }

  deleteVehicleTypes(vehicleTypes: VehicleType[]): void {
    const start = this.logStart('deleteVehicleTypes');
    this.decoree.deleteVehicleTypes(vehicleTypes);
    this.logEnd('deleteVehicleTypes', start);
  }

  //
  // Load Carriers
  //

  getLoadType(id: string): LoadType | undefined {
    const start = this.logStart('getLoadType');
    const loadType: LoadType | undefined = this.decoree.getLoadType(id);
    this.logEnd('getLoadType', start);
    return loadType;
  }

  addLoadTypes(loadTypes: LoadType[]): void {
    const start = this.logStart('addLoadTypes');
    this.decoree.addLoadTypes(loadTypes);
    this.logEnd('addLoadTypes', start);
  }

  deleteLoadTypes(loadTypes: LoadType[]): void {
    const start = this.logStart('deleteLoadTypes');
    this.decoree.deleteLoadTypes(loadTypes);
    this.logEnd('deleteLoadTypes', start);
  }

  //
  // Catalog
  //

  setCatalogTrafficRules(trafficRules: TrafficRulesCatalog): void {
    const start = this.logStart('setCatalogTrafficRules');
    this.decoree.setCatalogTrafficRules(trafficRules);
    this.logEnd('setCatalogTrafficRules', start);
  }

  getAllFileReferences(): FileReference[] {
    const start = this.logStart('getAllFileReferences');
    const fileReferences = this.decoree.getAllFileReferences();
    this.logEnd('getAllFileReferences', start);
    return fileReferences;
  }

  getFileReferences(type: FileReference['type']): FileReference[] {
    const start = this.logStart(`getFileReferences: ${type}`);
    const fileReferences = this.decoree.getFileReferences(type);
    this.logEnd(`getFileReferences: ${type}`, start);
    return fileReferences;
  }

  addFileReference(fileReference: FileReference): void {
    const start = this.logStart(`addFileReference: ${fileReference.url}`);
    this.decoree.addFileReference(fileReference);
    this.logEnd(`addFileReference: ${fileReference.url}`, start);
  }

  deleteFileReference(reference: FileReference): void {
    const start = this.logStart(`deleteFileReference: ${reference.url}`);
    this.decoree.deleteFileReference(reference);
    this.logEnd(`deleteFileReference: ${reference.url}`, start);
  }

  //
  // Floorplan
  //

  getFloorplan(): Floorplan | undefined {
    const start = this.logStart('getFloorplan');
    const floorplan: Floorplan | undefined = this.decoree.getFloorplan();
    this.logEnd('getFloorplan', start);
    return floorplan;
  }

  setFloorplan(floorplan: Floorplan): void {
    const start = this.logStart('setFloorplan');
    this.decoree.setFloorplan(floorplan);
    this.logEnd('setFloorplan', start);
  }

  //
  // Tracks
  //

  getTracks(): Track[] | undefined {
    const start = this.logStart('getTracks');
    const tracks: Track[] | undefined = this.decoree.getTracks();
    this.logEnd('getTracks', start);
    return tracks;
  }

  setTracks(tracks: Track[]): void {
    const start = this.logStart('setTracks');
    this.decoree.setTracks(tracks);
    this.logEnd('setTracks', start);
  }

  //
  // Selection
  //

  deleteSelection(selectedIds: string[]): void {
    const start = this.logStart('deleteSelection');
    this.decoree.deleteSelection(selectedIds);
    this.logEnd('deleteSelection', start);
  }

  mirrorSelectionHorizontally(selectedIds: string[]): void {
    const start = this.logStart('mirrorSelectionHorizontally');
    this.decoree.mirrorSelectionHorizontally(selectedIds);
    this.logEnd('mirrorSelectionHorizontally', start);
  }

  mirrorSelectionVertically(selectedIds: string[]): void {
    const start = this.logStart('mirrorSelectionVertically');
    this.decoree.mirrorSelectionVertically(selectedIds);
    this.logEnd('mirrorSelectionVertically', start);
  }

  rotateSelection90Degrees(selectedIds: string[]): void {
    const start = this.logStart('rotateSelection90Degrees');
    this.decoree.rotateSelection90Degrees(selectedIds);
    this.logEnd('rotateSelection90Degrees', start);
  }

  changeName(id: string, newName: string): boolean {
    const start = this.logStart('changeName');
    const result = this.decoree.changeName(id, newName);
    this.logEnd('changeName', start);
    return result;
  }

  //
  // Preferences
  //
  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey] {
    const start = this.logStart(`getPreference ${key}`);
    const preference = this.decoree.getPreference(key);
    this.logEnd(`getPreference ${key}`, start);
    return preference;
  }

  setPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
    value: FddmPreferenceValues[PreferenceKey],
  ): void {
    const start = this.logStart(`setPreference ${key}`);
    this.decoree.setPreference(key, value);
    this.logEnd(`setPreference ${key}`, start);
  }

  resetPreferences(): void {
    const start = this.logStart('resetPreferences');
    this.decoree.resetPreferences();
    this.logEnd('resetPreferences', start);
  }

  triggerAutosave(): void {
    const start = this.logStart('triggerAutosave');
    this.decoree.triggerAutosave();
    this.logEnd('triggerAutosave', start);
  }

  disposeState(): void {
    const start = this.logStart('disposeState');
    this.decoree.disposeState();
    this.logEnd('disposeState', start);
  }

  // private utility methods

  private logStart(methodName: string): number {
    const start: number = performance.now();
    logger.info(`Start -> ${methodName}`);
    return start;
  }

  private logEnd(methodName: string, start: number): void {
    logger.info(`End -> ${methodName} (${(performance.now() - start).toFixed(2)}ms)`);
  }
}
